import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { createAppKit } from '@reown/appkit';
import { WagmiAdapter } from '@reown/appkit-adapter-wagmi';
import { connect, disconnect, getAccount, reconnect, switchChain, watchAccount, watchChainId } from '@wagmi/core';
import { metaMask } from '@wagmi/connectors';
import type { GetAccountReturnType } from '@wagmi/core';
import { BehaviorSubject } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { LocalStorageService } from './local-storage.service';
import { setWagmiConfig } from '../utils/wagmi-client';
import { isAndroid, isMobile } from '../utils/platform';
import { ToastrService } from 'ngx-toastr';
import { Modal } from 'bootstrap';

/** User-facing copy when the wallet is not on the configured BSC chain (or Wagmi's states disagree). */
export const BSC_SWITCH_REQUIRED_MESSAGE = 'Please switch to BSC in your wallet';

@Injectable({
  providedIn: 'root'
})
export class WagmiConfigService {

  public supportedChains: any = environment.SUPPORTED_CHAINS;

  private accountSubject = new BehaviorSubject<any>(undefined);
  account$ = this.accountSubject.asObservable();

  private networkSubject = new BehaviorSubject<any>(undefined);
  network$ = this.networkSubject.asObservable();
  connectorInfo: any;
  private lastAddress: string | null = null;
  private wagmiConfig: any;
  private isSwitchingChain = false;
  /** Set while the active chain is unsupported; drives the one reload once it recovers. */
  private wasNetworkUnsupported = false;
  /** Guards against re-broadcasting `null` to subscribers that are already torn down. */
  private hasEmittedDisconnect = false;
  /** The registered MetaMask SDK connector on Android. Undefined everywhere else. */
  private androidMetaMaskConnector: any;
  /** Resolves once the boot-time reconnect has settled. See {@link waitForWalletSettled}. */
  private walletSettled?: Promise<void>;

  /**
   * wagmi's id for the MetaMask SDK connector, as written to `recentConnectorId` on connect.
   * Used to tell whether a stored session belongs to this connector before loading its SDK.
   */
  private static readonly METAMASK_SDK_CONNECTOR_ID = 'metaMaskSDK';


  constructor(
    private readonly router: Router,
    private readonly localStorageService: LocalStorageService,
    private readonly activeRoute: ActivatedRoute,
    private readonly toastrService: ToastrService
  ) {
    localStorageService.storeData('pageRefresh', true);
   }

  /**
   * Configure Reown AppKit and wagmi with our custom configuration.
   * Called once when the app boots.
   * This function sets up the AppKit modal and the account and network watchers.
   * When the account or network changes, it emits the new data to the component.
   * @returns {void}
   */
  public wagmiConfiguration() {
    const chains = this.supportedChains;
    const projectId = environment.WALLET_CONNECT_PROJECT_ID;

    // `url` is derived from the live origin rather than an environment constant — see
    // resolveDappOrigin() for why Verify makes that necessary.
    const metadata = {
      name: 'AOC',
      description: 'USDT Over Liquidity Contribution',
      url: WagmiConfigService.resolveDappOrigin(),
      icons: ['https://avatars.githubusercontent.com/u/37784886']
    };

    const adapter = new WagmiAdapter({ networks: chains, projectId });
    const config = adapter.wagmiConfig;
    this.wagmiConfig = config;
    // Publish the config so plain-function call sites can reach it: every @wagmi/core v2 action
    // takes it as the first argument.
    setWagmiConfig(config);

    const storedWallet = this.localStorageService.getDataByKey('walletAddress') ?? null;
    this.lastAddress = typeof storedWallet === 'string' ? storedWallet.toLowerCase() : null;

    createAppKit({
      adapters: [adapter],
      networks: chains,
      projectId,
      metadata,
      themeMode: 'dark',
      // Retained for other wallets, but note this does NOT fix MetaMask on Android, despite
      // looking like it should. AppKit only builds a universal link when the wallet's registry
      // entry carries a `link_mode`, and MetaMask's is null (verified against
      // api.web3modal.com/getWallets), so `formatNativeUrl` returns no universal link and
      // `onConnectMobile` falls back to the `metamask://` scheme regardless of this flag.
      // Android loses the pairing URI on that scheme, which is why the app opened with no
      // connection request. MetaMask on mobile is therefore connected by
      // `connectMetaMaskMobile()` instead, which opens the App Link directly.
      experimental_preferUniversalLinks: true,
      features: {
        analytics: true,
        // Android Chrome blocks the popup window email/social login open: the popup is spawned
        // after an `await` inside AppKit's handler, so by the time `window.open` runs the click
        // is no longer a trusted user gesture and the browser suppresses it, leaving the modal
        // stuck. Neither is offered by this dApp on any platform, so both stay off everywhere.
        email: false,
        socials: false
      }
    });

    //watch account activities
    //wallet-connected,disconnected,changed
    watchAccount(config, {
      onChange: (account) => this.handleAccountState(account)
    });

    // Drive the boot reconnect and seed subscribers only once it has settled. Seeding straight
    // from `getAccount()` here would be wrong: wagmi's initial status is a plain `disconnected`
    // (createConfig sets it literally), and reconnect only flips it later, asynchronously. So a
    // seed at this point reports every reload as a confirmed disconnect.
    this.walletSettled = this.settleInitialConnection(config);

    watchChainId(config, {
      onChange: (chainId) => {
        this.emitNetwork(chainId);
      }
    });

    // wagmi v2 only reports a chain id on change, so seed subscribers with the current one.
    this.emitNetwork(getAccount(config).chainId);
  }

  public getWagmiConfig() {
    return this.wagmiConfig;
  }

  /**
   * Restores a previous session on page load, then seeds subscribers with the settled result.
   *
   * @remarks
   * Exists because nothing else reliably restores the connection on a reload. AppKit runs its
   * own boot reconnect, but it passes an explicit connector list built from its internal
   * registry:
   *
   *   .map(connector => this.getWagmiConnector(connector.id))
   *   await reconnect(this.wagmiConfig, { connectors: wagmiConnectors })
   *
   * The Android MetaMask SDK connector is registered directly into the wagmi config rather than
   * through AppKit, so it is absent from that list and AppKit's attempt can never restore it.
   *
   * The retry matters. `reconnect()` guards on a module-level `isReconnecting` flag and returns
   * an empty result immediately if another reconnect is already in flight, so when AppKit wins
   * the race our call is silently a no-op — and AppKit's is the one that cannot see our
   * connector. Waiting for the status to settle and then trying once more covers that ordering,
   * and is a no-op in the ordering where our first attempt already succeeded.
   * @param config the wagmi config
   * @returns a promise that resolves when the account state is settled and emitted
   */
  private async settleInitialConnection(config: any): Promise<void> {
    try {
      const recentConnectorId = await config.storage?.getItem('recentConnectorId');

      // Nothing was ever connected on this device: no restore to attempt, and `disconnected` is
      // already the truthful answer.
      if (recentConnectorId) {
        // Only pay for the MetaMask SDK when the session being restored actually belongs to it.
        // Registering the connector loads the SDK, so doing it unconditionally would charge
        // every Android visitor for a wallet they may never connect.
        if (recentConnectorId === WagmiConfigService.METAMASK_SDK_CONNECTOR_ID) {
          this.ensureAndroidMetaMaskConnector(config);
        }

        try {
          await reconnect(config);
        } catch { }
        await this.waitForSettledStatus(config);

        if (getAccount(config).status !== 'connected') {
          try {
            await reconnect(config);
          } catch { }
          await this.waitForSettledStatus(config);
        }
      }
    } catch {
      // Fall through to seeding: a failed restore still needs subscribers told where they stand.
    } finally {
      this.handleAccountState(getAccount(config));
    }
  }

  /**
   * Resolves once wagmi's status leaves `connecting`/`reconnecting`.
   *
   * @remarks
   * Bounded by a timeout so a connector that never resolves its provider — a dead relay socket,
   * a wallet app that was never returned from — cannot leave the app waiting forever on a guard.
   * @param config the wagmi config
   * @param timeoutMs how long to wait before giving up
   * @returns a promise that resolves when settled, or on timeout
   */
  private waitForSettledStatus(config: any, timeoutMs = 10_000): Promise<void> {
    const isSettled = (status: string) => status === 'connected' || status === 'disconnected';
    if (isSettled(getAccount(config).status)) return Promise.resolve();

    return new Promise<void>((resolve) => {
      let unwatch: (() => void) | undefined;
      let finished = false;

      const finish = () => {
        if (finished) return;
        finished = true;
        clearTimeout(timer);
        unwatch?.();
        resolve();
      };

      const timer = setTimeout(finish, timeoutMs);
      unwatch = watchAccount(config, {
        onChange: (account) => {
          if (isSettled(account.status)) finish();
        }
      });
    });
  }

  /**
   * Waits for the boot-time restore to finish, then reports whether a wallet is connected.
   *
   * @remarks
   * For the call sites that guard on there being a session. Reading `hasWalletSession()`
   * synchronously in an `ngOnInit` races the restore and reports a connected user as
   * disconnected on every reload, which is what surfaced as "Wallet session not found".
   * @returns true when a wallet is connected once the restore has settled
   */
  public async waitForWalletSettled(): Promise<boolean> {
    try {
      await this.walletSettled;
    } catch { }
    return !!this.wagmiConfig && getAccount(this.wagmiConfig).status === 'connected';
  }

  /**
   * Connects MetaMask on Android through the MetaMask SDK connector.
   *
   * @remarks
   * Deliberately does not use WalletConnect pairing, which is what AppKit's modal does and what
   * fails here. Two facts made that unfixable from configuration:
   *
   * - MetaMask's WalletConnect registry entry is `mobile_link: "metamask://"` with
   *   `link_mode: null`. `CoreHelperUtil.formatNativeUrl` only returns a universal link when
   *   `link_mode` is set, so `onConnectMobile` always falls through to the custom scheme and
   *   `experimental_preferUniversalLinks` can never take effect for MetaMask.
   * - `metamask://wc?uri=...` does not carry the pairing URI through Chrome on Android, and
   *   `https://metamask.app.link/wc?uri=...` is a Branch.io link that answers 307 to
   *   `intent://open?link_click_id=...` — discarding the `uri` parameter outright. Both leave
   *   MetaMask open with no connection request, which was the reported symptom.
   *
   * The MetaMask SDK sidesteps the pairing URI entirely: it owns its own transport and its own
   * deep link (`useDeeplink` defaults to true), so there is no WalletConnect URI to lose. This
   * is the approach already proven on Android in the aoc-token-distribution-admin project,
   * which connects with `metaMask()` and has its WalletConnect option commented out.
   *
   * The connector is created here rather than registered in the adapter's connector list so
   * that desktop and iOS keep going through AppKit untouched.
   * @returns a promise that resolves once the wallet has approved, or rejects on failure
   */
  public async connectMetaMaskMobile(): Promise<void> {
    const config = this.wagmiConfig;
    if (!config) throw new Error('Wagmi config not initialised.');

    // Already connected: calling connect() again would throw.
    if (getAccount(config).status === 'connected') return;

    // Registers on first use, which is also where the SDK chunk gets loaded. This runs from the
    // connect button's click handler, so the cost lands on someone who has asked to connect.
    const connector = this.ensureAndroidMetaMaskConnector(config);
    if (!connector) throw new Error('MetaMask connector unavailable.');

    // Passing the registered instance, not a fresh `metaMask()` factory: handing `connect()` a
    // factory makes it call `setup()` again and mint another connector with a new uid, so each
    // attempt would leave behind an orphan that `reconnect()` could later restore from.
    await connect(config, { connector });
  }

  /**
   * Registers the MetaMask SDK connector into the wagmi config's connector list, on Android.
   *
   * @remarks
   * Registration is what makes the connection survive a page reload. `connect()` only calls
   * `config._internal.connectors.setup()` on a connector it is handed, which builds the
   * instance but never adds it to `config.connectors`. `reconnect()` iterates exactly that
   * list:
   *
   *   connectors.push(...config.connectors)
   *
   * so a connector that was only ever set up would be invisible to it. `recentConnectorId`
   * would be stored as the MetaMask SDK's id with nothing in the list answering to it, and the
   * user would come back from a refresh looking disconnected despite an intact session.
   *
   * The two-step setup-then-setState is the same sequence AppKit's own adapter uses in
   * `configureInternalConnector` to register the connectors it owns.
   *
   * Android-only, so desktop and iOS keep exactly the connector list AppKit built.
   *
   * Deliberately lazy, and idempotent so repeat callers share one instance. Registering runs
   * `setup()`, which resolves the connector's provider and so pulls in the `@metamask/sdk`
   * chunk — around half a megabyte. Doing that at bootstrap would bill every Android visitor
   * for it, including everyone who only reads a page and never touches a wallet. Instead it is
   * paid at the two moments it is actually needed: restoring a session that belongs to this
   * connector, and the user pressing connect.
   * @param config the wagmi config produced by the AppKit adapter
   * @returns the registered connector, or undefined off Android or on failure
   */
  private ensureAndroidMetaMaskConnector(config: any): any {
    // Android-only: iOS resolves `metamask://wc?uri=...` correctly, so AppKit's own hand-off
    // already works there and is left untouched.
    if (!isAndroid()) return undefined;
    if (this.androidMetaMaskConnector) return this.androidMetaMaskConnector;

    try {
      const connector = config._internal.connectors.setup(
        metaMask({
          dappMetadata: {
            name: 'AOC',
            // Same live origin the AppKit metadata uses, so the wallet shows one consistent
            // identity for the dApp whichever path connected it.
            url: WagmiConfigService.resolveDappOrigin()
          }
        })
      );
      config._internal.connectors.setState((prev: any[]) => [...prev, connector]);
      this.androidMetaMaskConnector = connector;
    } catch {
      // Registration is best-effort: without it the connect button is unavailable, but the
      // rest of the wallet layer must still come up.
    }

    return this.androidMetaMaskConnector;
  }

  /**
   * Routes a wagmi account snapshot to the right emission, keyed on `status` rather than the
   * `isConnected` boolean.
   *
   * @remarks
   * This is the Android Chrome fix. `isConnected` is false for all three of `connecting`,
   * `reconnecting` and `disconnected`, so the previous `!account.isConnected` check could not
   * tell an in-flight handshake apart from a real disconnect and broadcast `null` for all of
   * them. On Android that happens on every connect: Chrome freezes the tab while MetaMask is in
   * the foreground, and on return wagmi re-enters `reconnecting` before it resettles on
   * `connected`. Subscribers saw the intermediate `null`, treated it as a logout, cleared the
   * session and bounced the user home — mid-connect. Desktop and iOS never froze the tab, so
   * the transition passed too quickly to observe, which is why it only ever broke on Android.
   *
   * `connecting` and `reconnecting` are therefore held: the last good state stays on the
   * subject until the handshake actually settles.
   * @param account the snapshot from `watchAccount`/`getAccount`
   * @returns {void}
   */
  private handleAccountState(account: GetAccountReturnType): void {
    switch (account.status) {
      case 'connecting':
      case 'reconnecting':
        // In-flight, not a verdict. Emit nothing and leave the current value in place.
        return;

      case 'disconnected':
        this.emitDisconnected();
        return;

      case 'connected':
        this.emitConnected(account.address, account.chainId);
        return;
    }
  }

  /**
   * Broadcasts a settled disconnect, once.
   *
   * @remarks
   * De-duplicated because resume handling re-reads account state on every foreground event: an
   * already-disconnected user backgrounding and returning would otherwise re-broadcast `null`
   * each time and re-run every subscriber's teardown.
   * @returns {void}
   */
  private emitDisconnected(): void {
    if (this.hasEmittedDisconnect) return;

    this.hasEmittedDisconnect = true;
    this.lastAddress = null;
    this.connectorInfo = undefined;
    // Broadcast a confirmed disconnect (null) so subscribers can distinguish it from the
    // subject's initial "not yet resolved" state (undefined) and clear a stale walletAddress.
    this.accountSubject.next(null);
    this.localStorageService.storeData('pageRefresh', false);
  }

  /**
   * Broadcasts a connected account, and handles the wallet having been swapped underneath us.
   * @param address the connected address
   * @param chainId the chain it is on
   * @returns {void}
   */
  private emitConnected(address: string, chainId: number | undefined): void {
    const normalizedAddress = address.toLowerCase();
    const previousAddress = this.lastAddress;

    this.hasEmittedDisconnect = false;
    this.lastAddress = normalizedAddress;

    // Emitted shape is unchanged from the previous wagmi v1 store blob, so every existing
    // subscriber keeps reading `account.account` and `account.chainId` as before.
    this.connectorInfo = { account: address, chainId };
    this.accountSubject.next(this.connectorInfo);

    // A confirmed disconnect resets lastAddress to null (see emitDisconnected), which would
    // otherwise make the very next connect — even with a genuinely different wallet — read as
    // unchanged below. Fall back to the wallet a stored session's token actually belongs to,
    // which survives that reset: a token issued for the old wallet must never coexist with a
    // different wallet's connection.
    const storedWallet = (this.localStorageService.getDataByKey('walletAddress') ?? '')?.toLowerCase?.() ?? '';
    const hasStaleSession = !!storedWallet && storedWallet !== normalizedAddress && !!this.localStorageService.getToken();

    // Wallet changed anywhere in the app -> force logout + go home. Guarded on a *different*
    // address: a resume re-emitting the same account must not trip this.
    if ((previousAddress && normalizedAddress !== previousAddress) || hasStaleSession) {
      // Fire-and-forget, same as WalletAuthOrchestratorService's own handling of this event:
      // the localStorage clears inside run synchronously before the method's first `await`, so
      // ordering against the navigate() below is unaffected by not awaiting the wagmi disconnect.
      void this.disconnectAndClearSession();
      this.localStorageService.clearReferralIds();

      this.router.navigate(['']);
      const modalEl = Modal.getInstance(document.getElementById('transactionStatus'));
      if (modalEl) modalEl.hide();
    }

    this.localStorageService.storeData('pageRefresh', false);
  }

  /**
   * Re-reads wagmi's in-memory account state and re-emits it.
   *
   * @remarks
   * Called by {@link WalletResumeService} when the tab comes back to the foreground. Reads
   * synchronously from the live store — never from `localStorage['wagmi.store']`, whose
   * rehydration is exactly what a `setTimeout` would be racing against on a thawing Android tab.
   * @returns {void}
   */
  public syncAccountFromWagmi(): void {
    if (!this.wagmiConfig) return;
    this.handleAccountState(getAccount(this.wagmiConfig));
  }

  /**
   * Resolves the origin reported to wallets in the session proposal metadata.
   *
   * @remarks
   * Wallets run the proposal through WalletConnect's Verify API and compare this against the
   * origin actually serving the page. A build-time constant makes every origin other than the
   * one baked in — staging, pre-dev, localhost, tunnels — a mismatch that wallets surface as a
   * domain warning or, on some versions, refuse outright. Reading the live origin means each
   * deployment verifies as itself.
   * @returns the origin serving the app
   */
  private static resolveDappOrigin(): string {
    if (typeof window !== 'undefined' && window.location?.origin) {
      return window.location.origin;
    }
    // Non-browser fallback (SSR/prerender), where there is no live origin to read. Trimmed and
    // stripped of any trailing slash: the configured values are inconsistent about both, and
    // Verify compares origins exactly.
    return (environment.dev_url ?? '').trim().replace(/\/+$/, '');
  }

  /**
   * Emits the current network in the shape subscribers already expect.
   *
   * @remarks
   * wagmi v1 supplied a `chain.unsupported` flag; v2 dropped it and reports only the chain id,
   * so it is derived here against the configured chains. Keeping the emitted shape identical
   * means `network?.chain?.unsupported` checks around the app carry on working unchanged.
   * @param chainId the chain the wallet is currently on
   * @returns {void}
   */
  private emitNetwork(chainId: number | undefined): void {
    if (chainId === undefined) {
      this.networkSubject.next(undefined);
      return;
    }

    const unsupported = !this.supportedChains?.some((chain: any) => chain?.id === chainId);
    this.networkSubject.next({ chain: { id: chainId, unsupported } });

    if (unsupported) {
      this.wasNetworkUnsupported = true;
      this.switchNetwork();
    } else if (this.wasNetworkUnsupported) {
      // Recovered from an unsupported chain back to a supported one (the switchNetwork() above
      // succeeded, or the user switched back manually in their wallet).
      //
      // Deliberately NOT reloading here (this used to call `window.location.reload()`, replacing
      // what were previously two independent copies of the same reload in
      // wallet-connect.component.ts and dashboard-header.component.ts). A reload forces AppKit
      // to re-initialize from scratch, including restoring its own cached "current chain" state
      // — separate from wagmi's own state, which is already correct at this point since it's
      // what drove this very callback. On mobile, that restore can briefly read AppKit's
      // stale pre-switch cache before it resyncs with the wallet, which was re-triggering
      // AppKit's own "Switch Network" screen a second time right after the user had just
      // completed the switch. Leaving the app running (not reloaded) keeps AppKit's in-memory
      // state — already updated by this same chain-change event — intact, so there is no
      // fresh-boot moment for a stale cache to resurface.
      this.wasNetworkUnsupported = false;
    }
  }

  /**
   * Clears all locally-persisted session/wallet-identity state and disconnects the active
   * wagmi connector.
   *
   * @remarks
   * Single source of truth for "what does a full wallet+session teardown clear". Previously
   * this list was copy-pasted independently into `DashboardHeaderComponent.signOut()`,
   * `ContributeModalComponent.redirect()`, and both `HeaderNew*Component.openContributeModal()`,
   * and had drifted into three different subsets of keys across those four places. Callers keep
   * their own surrounding behaviour (a backend logout call, a toast, which page to navigate to,
   * spinner timing) — this only owns the teardown itself, so cleanup can never drift again.
   * @returns a promise that resolves once the wagmi disconnect has settled (best-effort; a
   *   rejected disconnect does not throw here, since the local state is already cleared by then)
   */
  public async disconnectAndClearSession(): Promise<void> {
    this.localStorageService.clearLocalStorage('warning-modal');
    this.localStorageService.clearLocalStorage('wagmi.store');
    this.localStorageService.clearLocalStorage('userId');
    this.localStorageService.removeToken();
    this.localStorageService.clearLocalStorage('userDetails');
    this.localStorageService.clearLocalStorage('userProfile');
    this.localStorageService.clearLocalStorage('walletAddress');
    // referralId / refId live in sessionStorage and are not session keys. Wallet-connect
    // (contribute CTA, 401, unrestorable wallet) calls this method and must leave them
    // alone so signup can still patch the code. They are cleared only by clearReferralIds()
    // when a wallet change or a logout redirects to `/`.
    this.localStorageService.clearLocalStorage('is_bounty_user');
    this.localStorageService.clearLocalStorage('onceLoginAnyAccount');
    this.localStorageService.clearLocalStorage('activeStep');
    // Per-user classification cached from user-scoped APIs and read directly by SAFAuthGuard,
    // SAFManifestAuthGuard, and amcGuard with no live re-check — must not leak to the next
    // wallet that logs in on this browser.
    this.localStorageService.clearLocalStorage('isAfricaUser');
    this.localStorageService.clearLocalStorage('is_manifestation_user');
    this.localStorageService.clearLocalStorage('contributorMemberType');
    
    try {
      await disconnect(this.wagmiConfig);
    } catch { }
  }

  /**
   * Switches the user's network to the configured BSC chain.
   * @remarks
   * Wraps the wagmi {@link switchChain} action. Guarded and caught: a rejected or unsupported
   * switch used to surface as an unhandled rejection, and concurrent callers could stack
   * multiple chain-switch prompts in the wallet.
   *
   * On success (especially on mobile WalletConnect) it waits until **both** the connection
   * chain id and the connector's own `getChainId()` report {@link environment.CHAIN_ID}
   * before resolving — otherwise a following `signMessage` can throw
   * `ConnectorChainMismatchError` while both sides still briefly disagree.
   * @param options.showToasts when false, callers that already toast failures stay silent here
   * @returns true when the wallet is on the expected chain after the attempt
   */
  public async switchNetwork(options: { showToasts?: boolean } = {}): Promise<boolean> {
    const showToasts = options.showToasts !== false;
    if (this.isSwitchingChain) {
      // Another switch is in flight — wait for it to finish, then report the settled chain.
      await this.waitForSwitchIdle();
      return this.isOnExpectedChain();
    }
    if (!this.wagmiConfig) return false;

    this.isSwitchingChain = true;
    try {
      await switchChain(this.wagmiConfig, { chainId: environment.CHAIN_ID });
      const settled = await this.waitUntilChainSettled(environment.CHAIN_ID);
      if (settled && showToasts) {
        this.toastrService.success('Switched to BSC');
      }
      if (!settled && showToasts) {
        this.toastrService.error(BSC_SWITCH_REQUIRED_MESSAGE);
      }
      return settled;
    } catch (error: any) {
      // 4001 is the user rejecting the prompt, which needs no error toast.
      if (showToasts && error?.code !== 4001) {
        this.toastrService.error(BSC_SWITCH_REQUIRED_MESSAGE);
      }
      return false;
    } finally {
      this.isSwitchingChain = false;
    }
  }

  /**
   * Ensures the connected wallet is on {@link environment.CHAIN_ID} before a signature or
   * contract write. Switches when needed and blocks the caller until both Wagmi chain
   * sources agree (or the switch fails / times out).
   * @param options.showToasts forwarded to {@link switchNetwork}; set false when the caller
   *   will toast the failure itself (e.g. `signLoginMessage`)
   * @returns true when it is safe to call `signMessage` / send a tx on the expected chain
   */
  public async ensureCorrectChain(options: { showToasts?: boolean } = {}): Promise<boolean> {
    if (!this.wagmiConfig) return false;
    if (await this.isOnExpectedChain()) return true;

    // Prompt the wallet; switchNetwork waits for settlement.
    return this.switchNetwork({ showToasts: options.showToasts !== false });
  }

  /**
   * True when both the connection's chain id and the connector's reported chain id equal
   * {@link environment.CHAIN_ID}. A lone match is not enough — that is exactly the
   * `ConnectorChainMismatchError` state Wagmi throws on mobile after a half-finished switch.
   */
  public async isOnExpectedChain(expectedChainId: number = environment.CHAIN_ID): Promise<boolean> {
    if (!this.wagmiConfig) return false;
    const { connectionChainId, connectorChainId } = await this.readChainIds();
    return connectionChainId === expectedChainId && connectorChainId === expectedChainId;
  }

  /**
   * Wagmi's `ConnectorChainMismatchError` (and closely worded shortMessages). Used by the
   * login orchestrator so the user sees {@link BSC_SWITCH_REQUIRED_MESSAGE} instead of the
   * raw "connector (id: 1) does not match the connection's chain (id: 1)" toast.
   */
  public static isChainMismatchError(error: unknown): boolean {
    const err = error as { name?: string; message?: string; shortMessage?: string } | null;
    if (!err) return false;
    if (err.name === 'ConnectorChainMismatchError') return true;
    const text = `${err.shortMessage ?? ''} ${err.message ?? ''}`.toLowerCase();
    return text.includes('does not match the connection') || text.includes('connectorchainmismatch');
  }

  /**
   * Polls until connection + connector both report `expectedChainId`, or the timeout elapses.
   * Mobile WalletConnect gets a longer window because the wallet app and the dApp tab update
   * out of band after the user confirms the switch.
   */
  private async waitUntilChainSettled(expectedChainId: number): Promise<boolean> {
    const timeoutMs = isMobile() ? 12_000 : 4_000;
    const intervalMs = 250;
    const deadline = Date.now() + timeoutMs;

    while (Date.now() < deadline) {
      if (await this.isOnExpectedChain(expectedChainId)) return true;
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
    return this.isOnExpectedChain(expectedChainId);
  }

  private async waitForSwitchIdle(): Promise<void> {
    const deadline = Date.now() + 15_000;
    while (this.isSwitchingChain && Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  }

  /**
   * Reads the two chain ids Wagmi compares when throwing `ConnectorChainMismatchError`.
   * `connectionChainId` comes from the active connection; `connectorChainId` is asked of the
   * connector itself (falls back to the connection id when `getChainId` is unavailable).
   */
  private async readChainIds(): Promise<{ connectionChainId?: number; connectorChainId?: number }> {
    const account = getAccount(this.wagmiConfig);
    const connectionChainId = account.chainId;
    let connectorChainId = connectionChainId;
    try {
      const fromConnector = await account.connector?.getChainId?.();
      if (typeof fromConnector === 'number') connectorChainId = fromConnector;
    } catch {
      // Connector may be mid-reconnect; treat as unsettled by leaving whatever we have.
    }
    return { connectionChainId, connectorChainId };
  }
}